import React from "react";

import { Button } from "~/components/button";
import { IconReloadOutlined } from "~/components/icon";
import { Join } from "~/components/join";
import { JoinItem } from "~/components/join/item";
import { Select } from "~/components/select";
import { SelectContent } from "~/components/select/content";
import { SelectItem } from "~/components/select/item";
import { SelectTrigger } from "~/components/select/trigger";
import { cx } from "~/tools/class-variance";
import { useHandler } from "~/tools/use-handler";
import { useInterval } from "~/tools/use-interval";

import { LOADING_DURATION_MS, useFullLoadingAnimation } from "./_use-full-loading-animation";
import { REFRESH_INTERVALS, type RefreshInterval, refreshIntervalToMs, toRefreshInterval } from "./utils";

export interface RefreshIntervalSelectorProps extends Omit<React.ComponentProps<typeof Join>, "onChange"> {
	loading?: boolean;
	disabled?: boolean;
	value: RefreshInterval;
	onChange: (value: RefreshInterval) => void;
	onRefresh: VoidFunction;
}

export const RefreshIntervalSelector = React.forwardRef<React.ComponentRef<typeof Join>, RefreshIntervalSelectorProps>(
	({ loading = false, disabled, value, onChange, onRefresh, ...props }, ref) => {
		const loadingFull = useFullLoadingAnimation(loading);
		const stableRefresh = useHandler(() => {
			if (!loading) {
				onRefresh();
			}
		});

		useInterval(
			() => {
				stableRefresh();
			},
			[stableRefresh],
			refreshIntervalToMs(value) ?? Number.POSITIVE_INFINITY,
			{
				pause: value === "off",
			},
		);

		return (
			<Join ref={ref} {...props}>
				<JoinItem>
					<Button
						className={cx("gap-2 bg-white px-3", loading && "cursor-wait")}
						onClick={stableRefresh}
						variant="tertiary"
					>
						<IconReloadOutlined
							className={cx("text-xs/0", loadingFull && "animate-spin")}
							style={{ animationDuration: `${LOADING_DURATION_MS}ms` }}
						/>
						Refresh
					</Button>
				</JoinItem>

				<Select
					disabled={disabled}
					onValueChange={(interval) => onChange(toRefreshInterval(interval) ?? "off")}
					value={value}
				>
					<JoinItem>
						<SelectTrigger className="gap-2">{value !== "off" && value}</SelectTrigger>
					</JoinItem>

					<SelectContent align="end" className="min-w-16" width="auto">
						{REFRESH_INTERVALS.map((interval) => (
							<SelectItem className="justify-center" key={interval} value={interval}>
								{interval === "off" ? "Off" : interval}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Join>
		);
	},
);
